import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "../ui/dialog";
import { SubmitHandler, useForm } from "react-hook-form";
import { useRecoilState } from "recoil";
import { brokerConnectModalAtom } from "@/Atoms/modal/DialogueAtoms";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { brokerFlagState } from "@/Atoms/auth/BrokerFlag";
import { useUserContext } from "@/contexts/userContext";
import axios from "axios";
import { getAccountBalances, useToastHelper } from "@/helpers/helperFunctions";
import { useTranslation } from "react-i18next";
import { CardContent, CardFooter, CardHeader, CardTitle } from "../ui/card";
import { HoverCard } from "@radix-ui/react-hover-card";
import { HoverCardContent, HoverCardTrigger } from "../ui/hover-card";
import { BiSolidHelpCircle } from "react-icons/bi";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import Loader from "../Loader";
import Image from "next/image";
import { BsExclamationCircle } from "react-icons/bs";
import { Loader2 } from "lucide-react";
import { MdClose, MdDone } from "react-icons/md";
import { FiCopy } from "react-icons/fi";
import { useDarkTheme } from "@/contexts/DarkTheme";
import {
  usePostRevokeBrokerKeys,
  usePostSubmitBrokerKeys,
} from "@/api/brokerApi";
import {
  BROKER_LOGO,
  CURRENT_BROKER,
  CURRENT_BROKER_ID,
  CURRENT_SERVER_NAME,
  isMT5,
} from "@/config/logos";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import TrafficSignalPill from "./TrafficSignalPill";

interface IFormValues {
  apiKey?: string;
  apiSecret?: string;
  passphrase?: string;
}

function BrokerConnectModal() {
  const connectForm = useForm<IFormValues>({
    mode: "onSubmit",
  });
  const [showConnectForm, setShowConnectForm] = useRecoilState(
    brokerConnectModalAtom
  );

  const [accountFunds, setAccountFunds] = useRecoilState(brokerFlagState);
  const [SCTab, setSCTab] = useState<any>(CURRENT_BROKER_ID);

  const { user: userInfo } = useUserContext();
  const { toast } = useToastHelper();
  const { t } = useTranslation("layout");
  const { t: ttt } = useTranslation("test");
  const [broker, setBroker] = useState<any>();
  const { darkTheme, setDarkTheme } = useDarkTheme();
  const [addNew, setAddNew] = useState<boolean>(false);
  const {
    mutateAsync: revokeBrokerKeys,
    isPending: isRevokeLoading,
    isSuccess: isRevokeSuccess,
    isError: isRevokeError,
    error: revokeError,
  } = usePostRevokeBrokerKeys();
  const {
    mutateAsync: submitBrokerKeys,
    isPending: isSubmitLoading,
    data: submitData,
    isSuccess: isSubmitSuccess,
    isError: isSubmitError,
    error: submitError,
  } = usePostSubmitBrokerKeys();
  const { brokerConnected, brokerConfigLoading, apiCallingLoading } =
    useMemo(() => {
      const connectedBroker = accountFunds?.liveFunds?.find(
        (item: any) => Number(item.broker_id) === Number(SCTab)
      );
      return {
        brokerConnected: !!connectedBroker,
        connectedBroker: connectedBroker?.broker_details,
        brokerConfigLoading: accountFunds?.loading,
        apiCallingLoading: accountFunds?.apiCalling,
      };
    }, [accountFunds, SCTab]);

  const isSaving = isSubmitLoading || isRevokeLoading;

  const handleRevokeBrokerKeys = async () => {
    await revokeBrokerKeys({
      userId: userInfo?.user_id,
      key: String(SCTab),
      connection_id: broker.connection_id,
    });
  };

  const handleSubmitBrokerKeys: SubmitHandler<IFormValues> = async (
    values,
    event
  ) => {
    event?.preventDefault(); // Prevent form submission default behavior if applicable

    const { apiKey, apiSecret, passphrase } = values as Required<IFormValues>;

    await submitBrokerKeys({
      userId: userInfo?.user_id,
      key: apiKey,
      secret: apiSecret ?? CURRENT_SERVER_NAME,
      brokerId: SCTab,
      passphrase: isMT5 ? passphrase : "s",
    });
  };

  useEffect(() => {
    if (isRevokeSuccess) {
      toast({ title: "Broker keys revoked successfully" });
      setAccountFunds((prev) =>
        prev.isConnected ? { ...prev, isConnected: false } : prev
      );
      getAccountBalances(userInfo, setAccountFunds, SCTab);
    } else if (isRevokeError && revokeError) {
      const errMessage = revokeError?.message;
      if (
        errMessage !== "Please upgrade to the pro plan to access this feature"
      ) {
        toast({
          title: errMessage || t("errorWhileDisconnectingTheBroker"),
          variant: "destructive",
        });
      } else {
        toast({
          title: t("errorWhileDisconnectingTheBroker"),
          variant: "destructive",
        });
      }
    }
  }, [isRevokeLoading, isRevokeSuccess, isRevokeError]);

  useEffect(() => {
    if (isSubmitSuccess) {
      toast({
        title: submitData?.data?.message,
      });
      setAccountFunds((prev) => ({
        ...prev,
        isConnected: true,
      }));
      connectForm.setValue("apiKey", "");
      connectForm.setValue("apiSecret", CURRENT_SERVER_NAME);
      connectForm.setValue("passphrase", "");
      getAccountBalances(userInfo, setAccountFunds, SCTab);
      setShowConnectForm({ isOpen: false });
    } else if (isSubmitError && submitError) {
      if (
        submitError?.message !==
        "Please upgrade to the pro plan to access this feature"
      ) {
        toast({
          title:
            axios.isAxiosError(submitError) &&
            submitError.response?.data?.detail
              ? submitError.response.data.detail
              : t("errorWhileConnectingTheBroker"),
          variant: "destructive",
        });
      }
    }
  }, [isSubmitLoading, isSubmitSuccess, isSubmitError]);

  useEffect(() => {
    if (brokerConnected) {
      const selectedBroker = accountFunds.connectedBrokers?.[0];
      if (selectedBroker) {
        setBroker(selectedBroker);
      }
    }
  }, [brokerConnected]);

  return (
    <Dialog
      defaultOpen={showConnectForm.isOpen}
      open={true}
      onOpenChange={(e) => {
        if (!brokerConnected) return;
        connectForm.clearErrors();
        connectForm.reset();
        setShowConnectForm({ isOpen: false });
      }}
    >
      <DialogContent hideCloseButton={!brokerConnected} className="pt-10">
        {brokerConnected && (
          <div
            className="absolute top-4 right-4 cursor-pointer"
            onClick={() => setShowConnectForm({ isOpen: false })}
          >
            <MdClose size={20} />
          </div>
        )}
        <Form {...connectForm}>
          <form
            className="h-full xl:flex xl:flex-col"
            method="post"
            onSubmit={
              !addNew && brokerConnected
                ? connectForm.handleSubmit(handleRevokeBrokerKeys)
                : connectForm.handleSubmit(handleSubmitBrokerKeys)
            }
            id="strategy-form"
          >
            <CardHeader className="px-0 ">
              {/* <div className="flex center items-center justify-between"> */}
              <div className="flex center items-center ">
                <CardTitle>
                  <div className="text-2xl whitespace-nowrap font-bold tracking-tight">
                    {t("connectTo")} {t("MT5 Account")}
                  </div>
                </CardTitle>
              </div>

              {!brokerConnected && (
                <>
                  <p className="text-muted-foreground">{`To unlock the full power of ${CURRENT_BROKER} enter your MT5 account credentials below. This step is essential to connect your account to the market data feed and enable both live and virtual trading via the API.`}</p>
                  <p className="text-muted-foreground">
                    {`Securely connect your account now to get started.`}
                  </p>
                </>
              )}
            </CardHeader>
            <CardContent className="px-0">
              <div className="flex flex-col mb-5 gap-5 w-full">
                <div className="w-full flex flex-col gap-2">
                  <FormField
                    control={connectForm.control}
                    name="apiKey"
                    rules={{
                      required:
                        !addNew && brokerConnected
                          ? false
                          : t("demoAccNumberIsRequired"),
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          <div className="flex relative gap-1">
                            <div>{t("MT5 Account Number")}</div>
                            <div className="cursor-pointer">
                              <HoverCard openDelay={100}>
                                <HoverCardTrigger
                                  className={brokerConnected ? "" : "hidden"}
                                >
                                  <BiSolidHelpCircle size={12} />
                                </HoverCardTrigger>
                                <HoverCardContent className="text-xs">
                                  {t("brokerAlreadyConnected")}
                                </HoverCardContent>
                              </HoverCard>
                            </div>
                          </div>
                        </FormLabel>
                        <FormControl>
                          <Input
                            autoComplete="off"
                            disabled={!addNew && brokerConnected}
                            placeholder={t("Enter account number here")}
                            {...field}
                            value={
                              !addNew && brokerConnected
                                ? broker?.account_number
                                : field.value || ""
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                {SCTab === 7 && (
                  <div className="w-full flex flex-col gap-2">
                    <FormField
                      control={connectForm.control}
                      name="passphrase"
                      rules={{
                        required:
                          !addNew && brokerConnected
                            ? false
                            : t("passwordIsRequired"),
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            <div className="flex group relative gap-1">
                              <div>{t("passphrase")}</div>
                              <div className="cursor-pointer">
                                <HoverCard openDelay={100}>
                                  <HoverCardTrigger
                                    className={brokerConnected ? "" : "hidden"}
                                  >
                                    <BiSolidHelpCircle size={12} />
                                  </HoverCardTrigger>
                                  <HoverCardContent className="text-xs">
                                    {t("brokerAlreadyConnected")}
                                  </HoverCardContent>
                                </HoverCard>
                              </div>
                            </div>
                          </FormLabel>
                          <FormControl>
                            <Input
                              autoComplete="off"
                              disabled={brokerConnected}
                              placeholder={t("passphrase1")}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
                {isMT5 && (
                  <div className="w-full flex flex-col gap-2">
                    <FormField
                      control={connectForm.control}
                      name="passphrase"
                      rules={{
                        required:
                          !addNew && brokerConnected
                            ? false
                            : ttt("passwordIsRequired"),
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            <div className="flex group relative gap-1">
                              <div>{t("MT5 Master Password")}</div>
                              <div className="cursor-pointer">
                                <HoverCard openDelay={100}>
                                  <HoverCardTrigger
                                    className={brokerConnected ? "" : "hidden"}
                                  >
                                    <BiSolidHelpCircle size={12} />
                                  </HoverCardTrigger>
                                  <HoverCardContent className="text-xs">
                                    {t("brokerAlreadyConnected")}
                                  </HoverCardContent>
                                </HoverCard>
                              </div>
                            </div>
                          </FormLabel>
                          <FormControl>
                            <Input
                              autoComplete="off"
                              disabled={!addNew && brokerConnected}
                              placeholder={t("Enter password here")}
                              {...field}
                              value={
                                !addNew && brokerConnected
                                  ? "********"
                                  : field.value
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                <div className="w-full flex flex-col gap-2">
                  <FormField
                    control={connectForm.control}
                    name="apiSecret"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          <div className="flex group relative gap-1">
                            <div>{t("Server")}</div>
                            <div className="cursor-pointer">
                              <HoverCard openDelay={100}>
                                <HoverCardTrigger
                                  className={brokerConnected ? "" : "hidden"}
                                >
                                  <BiSolidHelpCircle size={12} />
                                </HoverCardTrigger>
                                <HoverCardContent className="text-xs">
                                  {t("brokerAlreadyConnected")}
                                </HoverCardContent>
                              </HoverCard>
                            </div>
                          </div>
                        </FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={CURRENT_SERVER_NAME}
                          >
                            <SelectTrigger className="focus:ring-0 focus:outline-none">
                              <SelectValue placeholder={t("Select a server")} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={CURRENT_SERVER_NAME}>
                                {CURRENT_SERVER_NAME}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </CardContent>
            <CardFooter className="grow px-0 gap-4 flex-col">
              <div className="flex justify-end w-full">
                <TrafficSignalPill
                  stopped={!brokerConnected}
                  text={"Broker Connected"}
                  stoppedText={"Broker Disconnected"}
                ></TrafficSignalPill>
              </div>
              <div className="flex w-full h-full">
                <div className="flex flex-col w-full float-right h-full">
                  <div className="flex flex-col w-full lg:flex-row float-right">
                    {brokerConfigLoading ? (
                      <div className="flex w-full justify-center">
                        <Loader />
                      </div>
                    ) : (
                      <div className="w-full">
                        <div className="gap-2 float-left">
                          <div>
                            {SCTab === 7 ? (
                              <Image
                                width={115}
                                height={60}
                                src={
                                  darkTheme
                                    ? "/images/logos/blofin_dark.png"
                                    : "/images/logos/blofin_light.png"
                                }
                                alt="alpaca-logo"
                              />
                            ) : (
                              <Image
                                width={115}
                                height={60}
                                src={
                                  darkTheme
                                    ? BROKER_LOGO[CURRENT_BROKER].DARK.textLogo
                                    : BROKER_LOGO[CURRENT_BROKER].LIGHT.textLogo
                                }
                                alt="spark-logo"
                              />
                            )}
                          </div>
                        </div>
                        <Button
                          disabled={isSaving || apiCallingLoading}
                          className="text-sm gap-2 float-right"
                        >
                          {!addNew && brokerConnected
                            ? t("Revoke")
                            : t("connect")}
                          {isSaving || apiCallingLoading ? (
                            <Loader2 className="ml-2 h-5 w-5 animate-spin" />
                          ) : (
                            <BsExclamationCircle />
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                  {/* <div className="text-xs text-muted-foreground flex gap-1 items-center">
                    {t("Not sure how to connect the API.")}{" "}
                    <Button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowGuideModal({ isOpen: true, link: "" });
                      }}
                      variant="link"
                      className="p-0 text-xs text-[var(--marketplace-blue-new)]"
                    >
                      {t("Click here")}
                    </Button>
                  </div> */}
                </div>
              </div>
            </CardFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default BrokerConnectModal;
